import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { Loader2, Save, Settings, DollarSign, TrendingUp } from 'lucide-react';
import type { HaloSettings } from './types';

function estimateMediaValue(cpm: number, impressions: number): string {
  const value = (impressions / 1000) * cpm;
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function SettingsSection({ storeId }: { readonly storeId: number }) {
  const queryClient = useQueryClient();
  const [cpmInput, setCpmInput] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin', 'halo-settings-cpm', storeId],
    queryFn: async ({ signal }) => {
      const res = await api.get<HaloSettings>(
        `/api/v1/analytics/halo-settings?storeId=${storeId}`,
        { signal },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load settings');
      setCpmInput(String(res.data.cpmRate));
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (newCpm: number) => {
      const res = await api.patch<HaloSettings>(
        `/api/v1/analytics/halo-settings?storeId=${storeId}`,
        { cpmRate: newCpm },
      );
      if (!res.success) throw new Error(res.error ?? 'Failed to save settings');
      return res.data;
    },
    onSuccess: () => {
      setMessage({ type: 'success', text: 'CPM rate updated successfully' });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'halo-settings-cpm', storeId] });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err: Error) => {
      setMessage({ type: 'error', text: err.message });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  function handleSave() {
    const parsed = parseFloat(cpmInput);
    if (isNaN(parsed) || parsed < 0) {
      setMessage({ type: 'error', text: 'Please enter a valid CPM rate' });
      return;
    }
    saveMutation.mutate(parsed);
  }

  const currentCpm = parseFloat(cpmInput) || 0;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg bg-purple-50 dark:bg-purple-950/40 flex items-center justify-center">
          <Settings className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Settings</h3>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
        </div>
      ) : (
        <div>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300 block mb-2">CPM Rate (Cost per Mille)</label>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
                <DollarSign className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={cpmInput}
                onChange={(e) => setCpmInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                className="w-full pl-8 pr-20 py-2.5 text-sm border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all bg-gray-50 dark:bg-gray-950 focus:bg-white dark:focus:bg-gray-900"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500">/ 1K imp.</span>
            </div>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="px-4 py-2.5 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-2 shrink-0"
            >
              {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </button>
          </div>

          {/* Media Value Estimator */}
          {currentCpm > 0 && (
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <TrendingUp className="w-4 h-4 text-emerald-600 shrink-0" />
              <div>
                <span className="text-xs font-medium text-emerald-800">Est. media value per 100K impressions</span>
                <span className="text-sm font-semibold text-emerald-700 block">
                  {estimateMediaValue(currentCpm, 100_000)}
                </span>
              </div>
            </div>
          )}

          {message && (
            <div className={`flex items-center gap-2 mt-3 px-3 py-2 rounded-lg text-xs font-medium ${
              message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 dark:text-red-400 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
